import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  decodeDelta,
  DeltaDecodeError,
  useGetCommitQuery,
  type DecodedDelta,
  type DecodedNode,
  type DecodedRel
} from '@axilon-apps/domains-ontology-ui-state'
import {
  CountChips,
  DIFF_TABLE_PAGE_SIZE,
  DiffPagerRow,
  DiffRow,
  DiffTable,
  KindBadge,
  PropertyDiffTable,
  type CountChip
} from '../DiffTable'
import { propertyListingRows } from './entity-diff-rows'
import { formatLogicalId, relFullId, relLabel, urnDisplayName } from './entity-display'
import { parseCommitMessage } from './commit-dialog-utils'

export interface CommitDetailProps {
  hash: string
}

/** True when a decoded delta records nothing — the "no changes" save point. */
export function isEmptyDelta(delta: DecodedDelta): boolean {
  return (
    delta.nodesAdded.length === 0 &&
    delta.nodesChanged.length === 0 &&
    delta.nodesRemoved.length === 0 &&
    delta.relsAdded.length === 0 &&
    delta.relsChanged.length === 0 &&
    delta.relsRemoved.length === 0
  )
}

/**
 * What one commit changed, rendered from its stored delta in the shared diff
 * table language (the pattern the ingest diff established): count chips, one
 * flat `Kind | Type | Entity` row per change, and expandable property
 * listings. The delta is after-only — it records what the commit set things
 * to, not a before/after comparison — and removals carry logical ids only.
 * Values render from the typed decoder, so a 2^53+1 counter shows every digit.
 */
export function CommitDetail({ hash }: CommitDetailProps) {
  // Deltas are computed off the request path; the worker's merge wake-up
  // refetches this entry so the change set fills in when it lands.
  const { data, isLoading, error } = useGetCommitQuery(hash)

  const decoded = React.useMemo<{ delta: DecodedDelta } | { decodeError: string } | null>(() => {
    if (!data?.delta) return null
    try {
      return { delta: decodeDelta(data.delta) }
    } catch (err) {
      if (err instanceof DeltaDecodeError) return { decodeError: err.message }
      return { decodeError: 'Could not decode the commit delta.' }
    }
  }, [data])

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 py-3 text-xs text-ink-3">
        <Loader2 className="size-3 animate-spin" />
        Loading commit…
      </p>
    )
  }
  if (error || !data) {
    return (
      <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
        Failed to load commit {hash.slice(0, 8)}.
      </p>
    )
  }

  const { title, description } = parseCommitMessage(data.message)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <p className="font-mono text-xs break-all text-ink-3">{data.hash}</p>
        {title ? <p className="text-sm font-medium text-ink">{title}</p> : null}
        {description ? (
          <p className="text-xs whitespace-pre-wrap text-ink-3">{description}</p>
        ) : null}
        <p className="text-xs text-ink-3">
          {[data.author, new Date(data.createdAt).toLocaleString()].filter(Boolean).join(' · ')}
        </p>
      </div>

      {data.deltaPending ? (
        <p className="flex items-center gap-2 rounded-md bg-st-idle-bg/40 px-3 py-2 text-xs text-ink-3">
          <Loader2 className="size-3 animate-spin" />
          Computing changes…
        </p>
      ) : data.deltaOmitted ? (
        <p className="rounded-md bg-st-idle-bg/40 px-3 py-2 text-xs text-ink-3">
          The change set is too large to display
          {data.byteSize ? ` (${formatBytes(data.byteSize)})` : ''}.
        </p>
      ) : decoded && 'decodeError' in decoded ? (
        <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
          {decoded.decodeError}
        </p>
      ) : decoded ? (
        isEmptyDelta(decoded.delta) ? (
          <p className="rounded-md bg-st-idle-bg/40 px-3 py-2 text-xs text-ink-3">
            This commit recorded no changes.
          </p>
        ) : (
          <DeltaView delta={decoded.delta} />
        )
      ) : (
        <p className="text-xs text-ink-3">This commit carries no change set (a snapshot point).</p>
      )}
    </div>
  )
}

/** One flat delta row: everything needed to render and sort it. */
interface DeltaRow {
  key: string
  tone: 'add' | 'change' | 'remove'
  kindText: 'added' | 'modified' | 'deleted'
  /** Node or relationship — the table's entity-kind column. */
  entityKind: 'node' | 'rel'
  type: string
  name: string
  urn: string
  /** Property listing for the expansion; removals have none (id-only). */
  props?: DecodedNode['props']
  removed?: boolean
}

const KIND_ORDER: Record<DeltaRow['tone'], number> = { add: 0, change: 1, remove: 2 }

function nodeRow(node: DecodedNode, tone: 'add' | 'change'): DeltaRow {
  return {
    key: node.id,
    tone,
    kindText: tone === 'add' ? 'added' : 'modified',
    entityKind: 'node',
    type: node.labels[0] ?? 'Node',
    name: nodeDisplayName(node),
    urn: node.id,
    props: node.props
  }
}

function relRow(rel: DecodedRel, tone: 'add' | 'change'): DeltaRow {
  return {
    key: rel.id,
    tone,
    kindText: tone === 'add' ? 'added' : 'modified',
    entityKind: 'rel',
    type: rel.type,
    name: relLabel(rel),
    urn: relFullId(rel),
    props: rel.props
  }
}

function removedRow(id: string, entityKind: 'node' | 'rel'): DeltaRow {
  return {
    key: id,
    tone: 'remove',
    kindText: 'deleted',
    entityKind,
    type: entityKind === 'rel' ? 'Relationship' : urnKindLabel(id),
    name: entityKind === 'rel' ? formatLogicalId(id) : urnDisplayName(id),
    urn: entityKind === 'rel' ? formatLogicalId(id) : id,
    removed: true
  }
}

function DeltaView({ delta }: { delta: DecodedDelta }) {
  // One open entity row across the table: expanding any row collapses
  // whichever was open.
  const [openKey, setOpenKey] = React.useState<string | null>(null)
  const [page, setPage] = React.useState(0)

  const rows = React.useMemo(() => {
    const all: DeltaRow[] = [
      ...delta.nodesAdded.map((n) => nodeRow(n, 'add')),
      ...delta.relsAdded.map((r) => relRow(r, 'add')),
      ...delta.nodesChanged.map((n) => nodeRow(n, 'change')),
      ...delta.relsChanged.map((r) => relRow(r, 'change')),
      ...delta.nodesRemoved.map((id) => removedRow(id, 'node')),
      ...delta.relsRemoved.map((id) => removedRow(id, 'rel'))
    ]
    return all.sort(
      (a, b) =>
        KIND_ORDER[a.tone] - KIND_ORDER[b.tone] ||
        a.type.localeCompare(b.type) ||
        a.name.localeCompare(b.name)
    )
  }, [delta])

  // A different commit's delta starts from the first page.
  React.useEffect(() => setPage(0), [rows])

  const added = delta.nodesAdded.length + delta.relsAdded.length
  const changed = delta.nodesChanged.length + delta.relsChanged.length
  const removed = delta.nodesRemoved.length + delta.relsRemoved.length
  const chips: CountChip[] = []
  if (added > 0) chips.push({ text: `${added.toLocaleString()} added`, tone: 'add' })
  if (changed > 0) chips.push({ text: `${changed.toLocaleString()} modified`, tone: 'change' })
  if (removed > 0) chips.push({ text: `${removed.toLocaleString()} deleted`, tone: 'remove' })

  const pageCount = Math.max(1, Math.ceil(rows.length / DIFF_TABLE_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const shown = rows.slice(safePage * DIFF_TABLE_PAGE_SIZE, (safePage + 1) * DIFF_TABLE_PAGE_SIZE)

  return (
    <div className="flex flex-col gap-2">
      <p className="text-11 text-ink-3">
        Values shown are what this commit set them to (there is no before-image in a commit).
      </p>
      <CountChips chips={chips} />
      <DiffTable kindHeader="Change" entityKindHeader="Kind">
        {shown.map((row) => {
          const fieldCount = row.props ? Object.keys(row.props).length : 0
          return (
            <DiffRow
              key={row.key}
              badge={<KindBadge tone={row.tone}>{row.kindText}</KindBadge>}
              type={row.type}
              entityKind={row.entityKind}
              entity={
                row.removed ? <span className="text-ink-3 line-through">{row.name}</span> : row.name
              }
              entityTitle={row.urn}
              summary={
                row.props && fieldCount > 0
                  ? `(${fieldCount} field${fieldCount !== 1 ? 's' : ''})`
                  : undefined
              }
              open={openKey === row.key}
              onOpenChange={(open) => setOpenKey(open ? row.key : null)}
            >
              {row.props && fieldCount > 0 ? (
                <div className="flex flex-col gap-1">
                  <p className="px-2 font-mono text-10 break-all text-ink-3">{row.urn}</p>
                  <PropertyDiffTable columns="value" rows={propertyListingRows(row.props)} />
                </div>
              ) : undefined}
            </DiffRow>
          )
        })}
        <DiffPagerRow page={safePage} total={rows.length} onPageChange={setPage} />
      </DiffTable>
    </div>
  )
}

/**
 * The URN's kind segment as a display label — "folder:" → "Folder",
 * "udt-inst:" → "Udt Inst" — for grouping removals, which carry no labels.
 */
function urnKindLabel(urn: string): string {
  const segment = urn.split('/').pop() ?? urn
  const colon = segment.indexOf(':')
  if (colon <= 0) return 'Other'
  const kind = segment.slice(0, colon)
  if (kind === 'urn') return 'Other' // an un-pathed URN has no kind segment
  return kind
    .split('-')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ')
}

/** Prefers the entity's `name` prop; falls back to the URN's last segment. */
function nodeDisplayName(node: DecodedNode): string {
  const name = node.props.name
  if (typeof name === 'string' && name.length > 0) return name
  return urnDisplayName(node.id)
}

function formatBytes(n: number): string {
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MiB`
  if (n >= 1 << 10) return `${(n / (1 << 10)).toFixed(1)} KiB`
  return `${n} B`
}
